import { useEffect } from "react";
import type { DesignerEngine } from "@/core/engine/DesignerEngine";
import { isCuttingTool, useDesignerStore } from "@/state/designerStore";

interface Options {
  engine: DesignerEngine | null;
  onExport: () => void;
  /** Set by the viewport — turning the drawn line into a cut needs the camera. */
  cutRunner: React.MutableRefObject<(() => void) | null>;
}

/**
 * Global shortcuts. Brush size uses the bracket keys that every sculpting tool
 * uses, so the mouse wheel can stay on zoom where people expect it.
 */
export function useKeyboardShortcuts({ engine, onExport, cutRunner }: Options): void {
  useEffect(() => {
    if (!engine) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;

      const store = useDesignerStore.getState();
      const mod = event.ctrlKey || event.metaKey;

      if (mod && event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (event.shiftKey) engine.history.redo();
        else engine.history.undo();
        return;
      }
      if (mod && event.key.toLowerCase() === "y") {
        event.preventDefault();
        engine.history.redo();
        return;
      }
      if (mod && event.key.toLowerCase() === "e") {
        event.preventDefault();
        onExport();
        return;
      }
      if (mod) return;

      switch (event.key) {
        case "1":
          store.setTool("carve");
          break;
        case "2":
          store.setTool("smooth");
          break;
        case "3":
          store.setTool("fixings");
          break;
        case "4":
          store.setTool("cut");
          break;
        case "5":
          store.setTool("angle");
          break;
        case "6":
          store.setTool("flange");
          break;
        case "7":
          store.setTool("texture");
          break;
        case "0":
          // The perspective view cannot produce a straight cut, so the Cut
          // tool holds the camera on an elevation — same rule as the button.
          if (!isCuttingTool(store.tool)) store.setViewMode("free");
          break;
        case "t":
        case "T":
          store.setViewMode("top");
          break;
        case "f":
        case "F":
          store.setViewMode("front");
          break;
        case "b":
        case "B":
          store.setViewMode("back");
          break;
        case "l":
        case "L":
          store.setViewMode("left");
          break;
        case "r":
        case "R":
          store.setViewMode("right");
          break;
        case "Enter":
          if (isCuttingTool(store.tool)) {
            event.preventDefault();
            cutRunner.current?.();
          }
          break;
        case "[":
          store.scaleBrushRadius(1 / 1.12);
          break;
        case "]":
          store.scaleBrushRadius(1.12);
          break;
        case "?":
          store.setShowShortcuts(!store.showShortcuts);
          break;
        case "Escape":
          if (isCuttingTool(store.tool) && store.cutPoints.length > 0) store.clearCutPoints();
          else if (store.showShortcuts) store.setShowShortcuts(false);
          else if (store.confirmingReset) store.setConfirmingReset(false);
          else if (store.confirmingNew) store.setConfirmingNew(false);
          else store.selectFixing(null);
          break;
        case "Delete":
        case "Backspace":
          if (store.tool === "fixings" && store.selectedFixingId) {
            event.preventDefault();
            engine.removeFixing(store.selectedFixingId);
            store.selectFixing(null);
          }
          break;
        default:
          if (event.key.toLowerCase() === "d" && event.altKey) store.toggleDiagnostics();
          break;
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [engine, onExport, cutRunner]);
}
